import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Tooltip,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { getRoles, grantRole, revokeRole } from '../../services/userDetailService';
import { formatDateOnly } from '../../services/adminService';

const EDIT_PERMISSION = 'can_edit_roles';

const SOURCE_COLORS = {
  manual: 'warning',
  purchase: 'info',
  system: 'default',
};

const manualSourceOf = (role) => role.sources.find((source) => source.source === 'manual');
const otherSourcesOf = (role) => role.sources.filter((source) => source.source !== 'manual');

// granted_at is a full RFC3339 timestamp, not a bare date, so it must not be
// anchored — formatDateOnly only anchors a bare YYYY-MM-DD value.
const formatGrantedAt = (value) => formatDateOnly(value) ?? '';

function PermissionsTab({ detail, onRolesChanged }) {
  const permissions = JSON.parse(localStorage.getItem('userPermissions') || '[]');
  const mayEdit = permissions.includes(EDIT_PERMISSION);

  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [grantOpen, setGrantOpen] = useState(false);
  const [catalogue, setCatalogue] = useState([]);
  const [catalogueLoaded, setCatalogueLoaded] = useState(false);
  const [picked, setPicked] = useState('');

  const [revoking, setRevoking] = useState(null);

  // Only an existing manual grant makes a role ungrantable — the API answers
  // 409 for that one case. A role held through purchase or system is still
  // offered so an operator can pin it down with a manual grant of their own.
  const manuallyHeld = new Set(
    detail.roles.filter((role) => manualSourceOf(role)).map((role) => role.name),
  );
  const grantable = catalogue.filter((role) => !manuallyHeld.has(role.name));

  // Fetched lazily on first open per design.md, not on every open — a failed
  // fetch does not set catalogueLoaded, so the next open retries rather than
  // caching the failure.
  const openGrant = useCallback(async () => {
    setError(null);
    setPicked('');
    setGrantOpen(true);
    if (catalogueLoaded) return;
    try {
      setCatalogue(await getRoles());
      setCatalogueLoaded(true);
    } catch (err) {
      setError(err.message);
    }
  }, [catalogueLoaded]);

  const closeGrant = useCallback(() => {
    setError(null);
    setGrantOpen(false);
  }, []);

  const closeRevoke = useCallback(() => {
    setError(null);
    setRevoking(null);
  }, []);

  const confirmGrant = async () => {
    setBusy(true);
    setError(null);
    try {
      await grantRole(detail.id, picked);
      setGrantOpen(false);
      onRolesChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmRevoke = async () => {
    setBusy(true);
    setError(null);
    try {
      await revokeRole(detail.id, revoking.name);
      setRevoking(null);
      onRolesChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const revokingManual = revoking ? manualSourceOf(revoking) : null;
  const revokingOthers = revoking ? otherSourcesOf(revoking) : [];
  const displayName = [detail.first_name, detail.last_name].filter(Boolean).join(' ') || 'This user';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6">Roles</Typography>
          <Typography variant="caption" color="text.secondary">
            Only manually granted roles can be revoked here.
          </Typography>
        </Box>
        {mayEdit && (
          <Button variant="contained" onClick={openGrant}>
            + Add role
          </Button>
        )}
      </Box>

      {error && !grantOpen && !revoking && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {detail.roles.map((role) => {
        const manual = manualSourceOf(role);
        return (
          <Box
            key={role.name}
            data-testid={`role-row-${role.name}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 180 }}>
              {role.name}
            </Typography>
            {role.sources.map((source) => (
              <Tooltip
                key={source.source}
                title={
                  source.source === 'manual' && source.granted_by
                    ? `Granted by ${source.granted_by} on ${formatGrantedAt(source.granted_at)}`
                    : ''
                }
              >
                {/* Bold does not change the contrast ratio — all three source
                    colours already clear AA — but a 12px label reads better
                    with thicker strokes on a mid-tone chip. */}
                <Chip
                  label={source.source}
                  size="small"
                  color={SOURCE_COLORS[source.source] || 'default'}
                  sx={{ fontWeight: 600 }}
                />
              </Tooltip>
            ))}
            <Box sx={{ flexGrow: 1 }} />
            {mayEdit && manual && (
              <IconButton
                aria-label={`Revoke ${role.name}`}
                color="error"
                size="small"
                onClick={() => {
                  setError(null);
                  setRevoking(role);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      })}

      {/* Grant */}
      <Dialog open={grantOpen} onClose={closeGrant} fullWidth maxWidth="sm">
        <DialogTitle>{`Grant access to ${displayName}`}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="grant-role-label">Role</InputLabel>
            <Select
              labelId="grant-role-label"
              label="Role"
              value={picked}
              onChange={(event) => setPicked(event.target.value)}
            >
              {grantable.map((role) => (
                <MenuItem key={role.name} value={role.name}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {grantable.find((role) => role.name === picked)?.description ||
                'Roles this user already holds a manual grant of are not listed.'}
            </FormHelperText>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeGrant}>Cancel</Button>
          <Button variant="contained" disabled={!picked || busy} onClick={confirmGrant}>
            Grant
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke */}
      <Dialog open={Boolean(revoking)} onClose={closeRevoke} fullWidth maxWidth="sm">
        <DialogTitle>{revoking ? `Revoke ${revoking.name}?` : ''}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <DialogContentText>
            {revokingManual && revokingManual.granted_by
              ? `This removes the manual grant made by ${revokingManual.granted_by} on ${formatGrantedAt(
                  revokingManual.granted_at,
                )}.`
              : 'This removes the manual grant.'}
            {revokingOthers.length > 0 &&
              ` ${displayName} also holds ${revoking.name} through ${revokingOthers
                .map((source) => source.source)
                .join(' and ')}, so revoking this grant keeps the role in place.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRevoke}>Cancel</Button>
          <Button variant="contained" color="error" disabled={busy} onClick={confirmRevoke}>
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PermissionsTab;
